import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, telHref, waHref } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/Common";
import { Field, Honeypot, ConsentBox, SubmitBtn, SuccessBox, isEmail, isPhone } from "@/components/site/FormBits";

const INIT = { name: "", company: "", phone: "", email: "", city: "Aydın", district: "", service_type: "", facility_type: "",
  staff_count: "", shift: "", start_date: "", message: "", kvkk_ack: false, marketing_consent: false, website: "" };

function validate(v) {
  const e = {};
  if (v.name.trim().length < 2) e.name = "Ad soyad girin.";
  if (!isPhone(v.phone)) e.phone = "Geçerli bir telefon numarası girin (en az 10 hane).";
  if (!isEmail(v.email)) e.email = "Geçerli bir e-posta adresi girin.";
  if (v.city.trim().length < 2) e.city = "Hizmet alınacak ili girin.";
  if (!v.service_type) e.service_type = "Hizmet türünü seçin.";
  if (!v.kvkk_ack) e.kvkk_ack = "Devam etmek için aydınlatma metnini okuduğunuzu onaylayın.";
  return e;
}

const QUOTE_CRUMBS = [{ name: "Teklif Al" }];

function useQuoteForm() {
  const [v, setV] = useState(INIT);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setV((prev) => ({ ...prev, [k]: e?.target ? e.target.value : e }));
  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(v);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Lütfen işaretli alanları kontrol edin.");
      document.getElementById(Object.keys(e)[0])?.focus();
      return;
    }
    setLoading(true);
    try {
      await api.post("/public/quote", v);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return { v, set, errors, loading, done, submit };
}

function QInput({ k, q, ...rest }) {
  return <input id={k} value={q.v[k]} onChange={q.set(k)} className="field-input" data-testid={`quote-${k}`} type={rest.type} inputMode={rest.inputMode} autoComplete={rest.autoComplete} placeholder={rest.placeholder} />;
}

function QSelect({ k, q, opts, ph }) {
  return (
    <select id={k} value={q.v[k]} onChange={q.set(k)} className="field-input" data-testid={`quote-${k}`}>
      <option value="">{ph}</option>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function ContactFields({ q }) {
  const e = q.errors;
  return (
    <fieldset className="grid gap-5 sm:grid-cols-2">
      <legend className="mb-4 text-lg font-bold text-zinc-950">İletişim bilgileri</legend>
      <Field id="name" label="Ad Soyad" required error={e.name}><QInput k="name" q={q} autoComplete="name" /></Field>
      <Field id="company" label="Firma veya Site Adı"><QInput k="company" q={q} autoComplete="organization" /></Field>
      <Field id="phone" label="Telefon" required error={e.phone}><QInput k="phone" q={q} type="tel" inputMode="tel" autoComplete="tel" placeholder="05xx xxx xx xx" /></Field>
      <Field id="email" label="E-posta" required error={e.email}><QInput k="email" q={q} type="email" inputMode="email" autoComplete="email" /></Field>
    </fieldset>
  );
}

function ServiceFields({ q }) {
  const { settings: s, services } = useSite();
  const e = q.errors;
  return (
    <fieldset className="grid gap-5 sm:grid-cols-2">
      <legend className="mb-4 text-lg font-bold text-zinc-950">Hizmet bilgileri</legend>
      <Field id="city" label="Hizmet Alınacak İl" required error={e.city}><QInput k="city" q={q} /></Field>
      <Field id="district" label="İlçe"><QInput k="district" q={q} placeholder="Örn. Kuşadası" /></Field>
      <Field id="service_type" label="Hizmet Türü" required error={e.service_type}><QSelect k="service_type" q={q} opts={[...services.map((x) => x.title), "Diğer"]} ph="Seçiniz" /></Field>
      <Field id="facility_type" label="Tesis / İşletme Türü"><QSelect k="facility_type" q={q} opts={s.facility_types || []} ph="Seçiniz" /></Field>
      <Field id="staff_count" label="Tahmini Personel İhtiyacı" hint="Bilmiyorsanız boş bırakabilirsiniz."><QInput k="staff_count" q={q} placeholder="Örn. 2 kişi" /></Field>
      <Field id="shift" label="Vardiya / Çalışma Düzeni"><QSelect k="shift" q={q} opts={s.shift_options || []} ph="Seçiniz" /></Field>
      <Field id="start_date" label="Hizmet Başlama Tarihi"><QInput k="start_date" q={q} type="date" /></Field>
      <div className="sm:col-span-2">
        <Field id="message" label="Açıklama" hint="Tesisiniz, giriş sayısı, özel talepleriniz vb.">
          <textarea id="message" rows={5} maxLength={3000} value={q.v.message} onChange={q.set("message")} className="field-input" data-testid="quote-message" />
        </Field>
      </div>
    </fieldset>
  );
}

function QuoteForm({ q }) {
  return (
    <form onSubmit={q.submit} noValidate className="relative space-y-8 rounded-lg border border-zinc-200 bg-white p-5 sm:p-8" data-testid="quote-form">
      <Honeypot value={q.v.website} onChange={q.set("website")} />
      <ContactFields q={q} />
      <ServiceFields q={q} />
      <div className="space-y-4 border-t border-zinc-200 pt-6">
        <ConsentBox id="kvkk_ack" checked={q.v.kvkk_ack} onChange={q.set("kvkk_ack")} error={q.errors.kvkk_ack} to="/teklif-formu-aydinlatma-metni" linkText="Teklif Formu Aydınlatma Metni'ni" rest="okudum. *" />
        <ConsentBox id="marketing_consent" checked={q.v.marketing_consent} onChange={q.set("marketing_consent")} rest="Kampanya ve bilgilendirme amaçlı ticari elektronik ileti almak istiyorum. (İsteğe bağlı)" />
      </div>
      <SubmitBtn loading={q.loading} testid="quote-submit-btn">Teklif Talebini Gönder</SubmitBtn>
    </form>
  );
}

function QuoteDone({ phone }) {
  return (
    <SuccessBox testid="quote-success" title="Talebiniz alındı" text="Teşekkür ederiz. Talebiniz kaydedildi; en kısa sürede sizinle iletişime geçeceğiz. Acil durumlar için bizi arayabilirsiniz.">
      <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
        <a href={telHref(phone)} className="btn-red">Hemen Ara</a>
        <Link to="/" className="btn-outline">Ana Sayfaya Dön</Link>
      </div>
    </SuccessBox>
  );
}

function QuoteAside({ s }) {
  return (
    <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
      <div className="rounded-lg bg-[color:var(--ink)] p-6 text-sm text-zinc-300">
        <p className="text-base font-bold text-white">Hızlı iletişim</p>
        <p className="mt-2">Formla uğraşmak istemiyorsanız bizi arayın veya WhatsApp'tan yazın.</p>
        <div className="mt-4 flex flex-col gap-3">
          <a href={telHref(s.phone)} className="btn-red" data-testid="quote-aside-call">{s.phone}</a>
          <a href={waHref(s)} target="_blank" rel="noopener noreferrer" className="btn-ghost-dark" data-testid="quote-aside-whatsapp">WhatsApp ile yazın</a>
        </div>
      </div>
      <p className="text-xs leading-relaxed text-zinc-500">Teklifler; personel sayısı, çalışma saatleri, hizmet süresi ve tesis yapısına göre size özel hazırlanır.</p>
    </aside>
  );
}

export default function Quote() {
  const { settings: s } = useSite();
  const q = useQuoteForm();
  return (
    <>
      <Seo title={s.seo?.quote?.title} description={s.seo?.quote?.description} path="/teklif-al" />
      <PageHero title="Güvenlik Hizmeti Teklifi Alın" subtitle="Formu doldurun; ihtiyacınızı değerlendirip size özel planlama ve teklif için iletişime geçelim." image="/img/checkpoint.webp" crumbs={QUOTE_CRUMBS} />
      <div className="bg-zinc-50">
        <div className="container-x grid gap-10 py-12 sm:py-16 lg:grid-cols-[1fr_320px]">
          {q.done ? <QuoteDone phone={s.phone} /> : <QuoteForm q={q} />}
          <QuoteAside s={s} />
        </div>
      </div>
    </>
  );
}
